import { pipe } from '@/utils/pipe';

/**
 * Match URLs in a string: /(?<!\()https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,63}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)(?!\))/g;
 *
 * This regex identifies URLs that:
 * - Start with "http://" or "https://".
 * - Optionally include "www.".
 * - Contain a valid domain name with 2 to 256 characters.
 * - End with a top-level domain (TLD) of 2 to 63 characters.
 * - May include additional valid URL path, query, or fragment components.
 *
 * Exclusions:
 * - URLs enclosed in parentheses are not matched.
 *
 * Example matches:
 * - "https://example.com"
 * - "http://www.example.com/path?query=123"
 *
 * Example non-matches:
 * - "(https://example.com)"
 * - "example.com" (missing protocol)
 */
const URL_REGEX =
  /(?<!\()https?:\/\/(www\.)?[-a-zA-Z0-9@:%._+~#=]{2,256}\.[a-z]{2,63}\b([-a-zA-Z0-9@:%_+.~#?&//=]*)(?!\))/g;

/**
 * Relative Markdown Links: (\[([^\]]+)\]\((\/[^\)]+)\))
 *
 * This part matches relative markdown links, like `[Home page](/home)`:
 * - Match the literal square brackets that wrap the link text.
 * - Capture the text inside the square brackets (the link text).
 * - Match the literal parentheses that wrap the URL.
 * - The literal forward slash `/` which marks the start of a relative URL.
 * - Any characters except the closing parenthesis `)`, i.e., the relative URL path.
 *
 * Example matches:
 *  - "[Home](/home)"
 *  - "[Contact](/contact)"
 *
 * Example non-matches:
 *  - "[Home](https://example.com/home)" (absolute URL, not a relative URL)
 */
const RELATIVE_MARKDOWN_LINK_REGEX = /\[([^\]]+)\]\((\/[^)]+)\)/g;

const formatUrlAsMarkdownLink = (url: string) => `[${url}](${url})`;
const formatRelativeLink =
  (baseUrl: string) => (_: string, label: string, path: string) =>
    `[${label}](${baseUrl}${path})`;

const formatUrl = (s: string) =>
  s.replaceAll(URL_REGEX, formatUrlAsMarkdownLink);
const formatRelativeUrl = (baseUrl: string) => (s: string) =>
  s.replaceAll(RELATIVE_MARKDOWN_LINK_REGEX, formatRelativeLink(baseUrl));

export const transformMarkdown = (text: string, baseUrl = '') =>
  pipe(formatUrl, formatRelativeUrl(baseUrl))(text);
